import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { NewExercise } from '../../../../../core/components/organism/NewExercise';
import {
  IAlternative,
  INewExercise,
} from '../../../../../core/models/new_exercise';
import { useAnswerExercise } from '../../../hooks/DailySchedule/useAnswerExercise';
import { useGetExerciseAnswer } from '../../../hooks/DailySchedule/useGetExerciseAnswer';
import { IPAExerciseDto } from '../../../model/dailySchedule';
import { useDailyScheduleContentStore } from '../../../stores/useDailyScheduleContent';
import useDoneDiscurviseExercise from '../../../hooks/DailySchedule/useDoneDiscurviseExercise';
import { getDoneFromPAResponse } from '../../../utils/contentDone';

export function PAExercisePage() {
  const { scheduleId } = useParams();

  const { mutateAsync: markDiscursiveExerciseAsDone, isLoading: isLoadingMarkDiscursiveExerciseAsDone } = useDoneDiscurviseExercise();
  const { mutateAsync: answerExercise, isLoading: isLoadingAnswer } = useAnswerExercise();
  const { mutateAsync: getExerciseAnswer, isLoading: isLoadingGetAnswer } = useGetExerciseAnswer();

  const { selectedContent, setContentList, contentList } = useDailyScheduleContentStore((s) => ({
    selectedContent: s.selectedContent,
    setContentList: s.setContentList,
    contentList: s.contentList,
  }));

  if (!selectedContent) {
    return (
      <div className='flex h-full items-center justify-center'>
        <Typography>Não foi possível carregar o exercicio.</Typography>
      </div>
    );
  }

  async function checkAlternativeAndMarkAsDone(alternative: IAlternative[]) {
    if (!selectedContent || !scheduleId) return;

    const response = await answerExercise({
      cronogramaId: scheduleId,
      exerciseId: selectedContent.content.id,
      alternativeId: alternative.map((it) => it._id),
    });

    const done = getDoneFromPAResponse(response);

    const newContentList = contentList.map((item) => {
      if (item.content.id === selectedContent.content.id) {
        return { ...item, content: { ...item.content, done, userAnswer: alternative.map((it) => it._id), userAnswerCorrect: response.resultado.acertou } };
      }
      return item;
    });
    setContentList(newContentList);

    return response.resultado.acertou;
  }

  async function handleMarkDiscursiveExerciseAsDone() {
    if (!selectedContent || !scheduleId) return;
    
    const response = await markDiscursiveExerciseAsDone({
      cronogramaId: scheduleId,
      exerciseId: selectedContent.content.id,
    });

    const done = getDoneFromPAResponse(response);

    const newContentList = contentList.map((item) => {
      if (item.content.id === selectedContent.content.id) {
        return { ...item, content: { ...item.content, done } };
      }

      return item;
    });

    setContentList(newContentList);
  }

  async function getAnswer() {
    if (!selectedContent || !scheduleId) return;
    const response = await getExerciseAnswer({
      cronogramaId: scheduleId,
      exerciseId: selectedContent.content.id,
    });

    return response.respostas;
  }

  const exercise: INewExercise | null = useMemo(() => {
    if (!selectedContent?.content) {
      return null;
    }

    const castedContent = selectedContent.content as IPAExerciseDto;

    return {
      alternatives: castedContent.alternatives.map((it) => ({
        ...it,
        text: it.text
          ? it.text.replaceAll(
              '{STORAGE_BASE_URL}',
              'https://storage.aprovatotal.com.br'
            )
          : '',
      })),
      ancestors: {
        area: {
          id: castedContent.areaId,
          slug: castedContent.slug_area,
        },
        level: {
          id: castedContent.levelId,
          slug: castedContent.slug_level,
        },
        subarea: {
          id: castedContent.subareaId,
          slug: castedContent.slug_subarea,
        },
      },
      correctPercentage: castedContent.correctAnswersPercentage,
      difficulty: castedContent.difficulty,
      exercise_type: castedContent.type,
      favorite: castedContent.favorite,
      id: castedContent.id,
      resolution: castedContent.resolution
        ? castedContent.resolution.replaceAll(
            '{STORAGE_BASE_URL}',
            'https://storage.aprovatotal.com.br'
          )
        : '',
      resolution_video: castedContent.resolution_video,
      slug: castedContent.slug,
      statement: castedContent.statement
        ? castedContent.statement.replaceAll(
            '{STORAGE_BASE_URL}',
            'https://storage.aprovatotal.com.br'
          )
        : '',
      subjects: [], //TODO ADD SUBJECTS
      title: castedContent.title,
      userAnswer: castedContent.userAnswer || null,
      userAnswerCorrect: castedContent.userAnswerCorrect || null,
      previouslyAnswered: castedContent.answeredOnServer ?? false,
    };
  }, [selectedContent]);

  return (
    <div className='p-8'>
      <NewExercise
        exercise={exercise}
        checkAlternative={checkAlternativeAndMarkAsDone}
        isLoading={isLoadingAnswer}
        getExerciseAnswer={getAnswer}
        getExerciseAnswerLoading={isLoadingGetAnswer}
        originPlan={true}
        discursiveAnswer={handleMarkDiscursiveExerciseAsDone}
      />
    </div>
  );
}
